/// <amd-module name="VSS/Platform/KeyboardShortcuts" />
import * as mousetrap from "mousetrap";

import { ObservableValue } from "VSS/Core/Observable";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";

import { IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { IContributedCommand, IVssContributionService } from "VSS/Platform/Contribution";
import { publishEvent } from "VSS/Platform/Telemetry";

declare global {
    interface Window {
        Mousetrap: MousetrapStatic;
    }
}

const KeyboardShortcutsDisabledKey = "KeyboardShortcutsDisabled";

export interface IEKeyboardEvent extends KeyboardEvent {
    returnValue: boolean;
}

export type KeyboardAction = (e: IEKeyboardEvent, combo: string) => void;

export interface IShortcutDefinition {
    /**
     * Description of the shortcut
     */
    description: string;

    /**
     * Action which gets called when shortcut is pressed
     */
    action: KeyboardAction;

    /**
     * The Dom Element to bind the shortcut to. It can either be an Element or a query selector string
     */
    element?: Element | string;

    /**
     * Defaults to false. Pass in True if you would like the shortcut to be hidden from the help dialog
     */
    hideFromHelpDialog?: boolean;

    /**
     * Defaults to false. Use true in the rare case that you want the last key of the chord to propagate to the focused element
     */
    allowPropagation?: boolean;

    /**
     * List combos which you want to be always active even if the user has focus on an input box
     */
    globalCombos?: string[];

    /**
     * Is this a navigation shortcurt? If so, we will handle reseting shortcuts so
     * if this is an in memory navigation you don't have mixed shortcut state
     */
    isPageNavigationShortcut?: boolean;
}

export interface IShortcut extends IShortcutDefinition {
    /**
     * Shortcut combinations that map to this action
     */
    combos: string[];

    /**
     * Shortcut combinations to display on the help dialog
     */
    combosToDisplay?: string[];
}

/**
 * A group of hotkeys that are directly attached to the document. Deleting the current group removes all hotkey bindings, so we need to reattach to the document the group that is further down the stack from the current group.
 */
export interface IReassignableShortcutGroup {
    /**
     *  The name of the group
     */
    name: string;

    /*
     * Sequence number assigned to the Group when it is added to the list of groups
     */
    sequenceNumber: number;

    /**
     * The list of shortcuts in the group
     */
    shortcuts: IShortcut[];
}

export interface IShortcutGroup {
    /**
     *  The name of the group
     */
    name: string;

    /**
     * The list of shortcuts in the group
     */
    shortcuts: IShortcut[];
}

interface IMousetrapShortcutGroup extends IShortcutGroup {
    /**
     * The list of shortcuts in the group
     */
    shortcuts: IMousetrapShortcut[];
}

interface IMousetrapReassignableShortcutGroup extends IReassignableShortcutGroup {
    /**
     * The list of shortcuts in the group
     */
    shortcuts: IMousetrapShortcut[];
}

interface IMousetrapShortcut extends IShortcut {
    /**
     * Mousetrap Instance of wrapped element if applicable
     */
    mousetrapInstance?: MousetrapInstance;
}

export interface IContributedShortcut {
    /**
     * The shortcut group this short cut belongs to.
     */
    group: string;

    /**
     * The combination of keys which will invoke the shortcut.  You can provide more than one
     * combination which will invoke the same the action.
     */
    combos: string[];

    /**
     * Descritpion of what the short cut will do wwhen invoked.
     */
    description: string;

    /**
     * Id of a contributed command to exeucte when the shortcut is executed
     */
    commandId?: string;

    /**
     * The command to execute when  when the shortcut is executed
     */
    command?: IContributedCommand;

    /**
     * Is this a navigation shortcurt? If so, we will handle reseting shortcuts so
     * if this is an in memory navigation you don't have mixed shortcut state
     */
    isPageNavigationShortcut?: boolean;
}

/**
 * Serivce which will manage shortcuts.  it will load shortcuts defined via contributions and also allow
 * you to register shortcuts dynamically.
 */
export interface IVssKeyboardShortcutService extends IVssService {
    /**
     * Returns the set of shortcut groups which have been registered.
     */
    getShortcutGroups(): IShortcutGroup[];

    /**
     * Returns the reassignable shortcut group with gratest sequence number for a given group name.
     * @param groupName - Name of the group to find
     */
    findNextReassignableGroupByName(groupName: string): IMousetrapReassignableShortcutGroup | undefined;

    /**
     * Returns the set of shortcut groups which have been registered.
     */
    getShortcutsEnabled(): ObservableValue<boolean>;

    /**
     * Returns set of global combos.
     */
    getGlobalCombos(): string[];

    /**
     * Registers a single shortcut combination.
     *
     * @param group - Group which the shortcurt is associated with.
     * @param combo - Combination of keystrokes used to invoke the shortcut
     * @param description - Description of the shortcut
     * @param action - Action to perform when the shortcut is invoked.
     * @param allowPropagation - Defaults to false. Use true in the rare case that you want the last key of the chord to propagate to the focused element.
     */
    registerShortcut(group: string, shortcut: IShortcut): void;

    /** 
     * Registers a single shortcut combination.
     * 
     * @param group - Group which the shortcurt is associated with. 
     * @param combo - Combination of keystrokes used to invoke the shortcut
     */
    registerReassignableGroupShortcut(group: { name: string; sequenceNumber: number }, shortcut: IShortcut): void;

    /**
     * Remvoes a specfic shortcut from a group.
     *
     * @param group - Group the shortcut is associated with.
     * @param combo - Keystroke combination to invoke the shortcut.
     */
    unRegisterShortcut(group: string, combo: string): void;

    /**
     * Returns the set of shortcut groups which have been registered.
     */
    setShortcutsEnabled(newValue: boolean): void;

    /**
     * Removes a specfic group and all shortcuts registered within it.
     *
     * @param group - Group to remove from the shortcut list.  All shortcuts within group will be removed.
     */
    removeShortcutGroup(group: string): void;

    /**
     * Removes a specfic group and all shortcuts registered within it. All shortcuts within group will be removed. If there are multiple groups with the same name, the next group with the same name will be used.
     * 
     * @param groupName - Group name of the group to remove.  
     * @param sequenceNumber -Sequence number of the group to remove
     */
    removeReassignableShortcutGroup(groupName: string, sequenceNumber: number): void;
}

class VssKeyboardShortcutService extends VssService implements IVssKeyboardShortcutService {
    private _registeredCombos: { [registeredCombo: string]: boolean };
    private _shortcutsGroups: { [group: string]: IMousetrapShortcutGroup };
    private _reassignableShortcutsGroups: IMousetrapReassignableShortcutGroup[];

    private _globalCombos: { [globalCombo: string]: boolean };
    private _originalStopCallback = window.Mousetrap.prototype.stopCallback;
    private _shortcutsEnabled: ObservableValue<boolean> = new ObservableValue<boolean>(true);

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const self = this;
        window.Mousetrap.prototype.stopCallback = function (e: Event, element: HTMLElement, combo: string, sequence: string) {
            if (self._globalCombos[combo] || self._globalCombos[sequence]) {
                return false;
            }

            // Check if the element has the attribute indicating we should only process global shortcuts for it
            // This is used when we are marshalling an event from a content editable IFrame to the parent frame
            // If we see this attribute here we will block the shortcut since we know the shortcut is not global
            if (element.getAttribute("data-onlyglobalshortcuts") === "true") {
                return true;
            }

            // We need to pass the 'this' from the function invocation since its the Mousetrap instance
            return self._originalStopCallback.call(this, e, element, combo);
        };

        this.initializeService();
    }

    public _serviceRestart(pageContext: IVssPageContext) {
        super._serviceRestart(pageContext);

        this.initializeService();
    }

    public getShortcutGroups(): IShortcutGroup[] {
        return Object.keys(this._shortcutsGroups).map(key => this._shortcutsGroups[key]);
    }

    public getShortcutsEnabled(): ObservableValue<boolean> {
        return this._shortcutsEnabled;
    }

    public getGlobalCombos(): string[] {
        return Object.keys(this._globalCombos);
    }

    public findNextReassignableGroupByName(nameToFind: string): IMousetrapReassignableShortcutGroup | undefined {
        let maxNumberObj: IMousetrapReassignableShortcutGroup | undefined = undefined;
        let maxNumber = Number.MIN_SAFE_INTEGER;

        for (const obj of this._reassignableShortcutsGroups) {
            if (obj.name === nameToFind && obj.sequenceNumber > maxNumber) {
                maxNumber = obj.sequenceNumber;
                maxNumberObj = obj;
            }
        }

        return maxNumberObj;
    }

    public registerReassignableGroupShortcut(group: { name: string; sequenceNumber: number }, shortcut: IShortcut): void {
        // Ensure the group is initialized
        let shortcutsGroup = this._reassignableShortcutsGroups.find(g => g.name === group.name && g.sequenceNumber === group.sequenceNumber);

        if (!shortcutsGroup) {
            shortcutsGroup = { name: group.name, sequenceNumber: group.sequenceNumber, shortcuts: [] };
            this._reassignableShortcutsGroups.push(shortcutsGroup);
        }

        const mousetrapShortcut: IMousetrapShortcut = this.makeMoustrapShortcut(group.name, shortcut);

        shortcutsGroup.shortcuts.push(mousetrapShortcut);
    }

    private makeMoustrapShortcut(groupname: string, shortcut: IShortcut) {
        const recordTelemetry = (combo: string) => {
            publishEvent(this.pageContext, "KeyboardShortcuts", "ShortcutInvoked", {
                group: groupname,
                shortcut: combo
            });
        };

        let mtInstance: MousetrapInstance | undefined;
        const wrappedAction = (e: IEKeyboardEvent, combo: string) => {
            if (this._shortcutsEnabled.value) {
                recordTelemetry(combo);

                // If this is a navigation shortcut then reset shortcut manager state
                // so no more navigations can occur and a new ajax rendered page is starting fresh
                if (shortcut.isPageNavigationShortcut) {
                    this.reset();
                }

                shortcut.action(e, combo);
                return shortcut.allowPropagation || false;
            }
            return false;
        };

        let shortcutElement: Element | null | undefined;
        if (typeof shortcut.element === "string") {
            try {
                // document.querySelector can throw if the selector string is not of the right format.
                shortcutElement = document.querySelector(shortcut.element);
            } catch {
                shortcutElement = null;
            }
        } else {
            shortcutElement = shortcut.element;
        }

        if (shortcutElement) {
            mtInstance = window.Mousetrap(shortcutElement);
            mtInstance.bind(shortcut.combos, wrappedAction);
        } else {
            // Register with the underlying Mousetrap library which handles the key binding logic
            window.Mousetrap.bind(shortcut.combos, wrappedAction);
        }

        // Add global combo if specified. These will run regardless if a input box has focus
        const globalCombos = shortcut.globalCombos || [];
        for (const combo of shortcut.combos) {
            if (globalCombos.indexOf(combo) >= 0) {
                this._globalCombos[combo] = true;
            }
        }

        // Prevent the same shortcut combos from being shown in the help dialog more than once
        const combosToAddToHelpDialog: string[] = [];
        for (const combo of shortcut.combos) {
            if (!this._registeredCombos[combo]) {
                combosToAddToHelpDialog.push(combo);
                this._registeredCombos[combo] = true;
            }
        }

        const mousetrapShortcut: IMousetrapShortcut = {
            ...shortcut,
            combos: shortcut.combos,
            combosToDisplay: shortcut.hideFromHelpDialog ? [] : combosToAddToHelpDialog,
            mousetrapInstance: mtInstance
        };

        return mousetrapShortcut;
    }

    public registerShortcut(group: string, shortcut: IShortcut): void {
        // Ensure the group is initialized
        if (!this._shortcutsGroups[group]) {
            this._shortcutsGroups[group] = { name: group, shortcuts: [] };
        }

        const mousetrapShortcut: IMousetrapShortcut = this.makeMoustrapShortcut(group, shortcut);

        this._shortcutsGroups[group].shortcuts.push(mousetrapShortcut);
    }

    public unRegisterShortcut(group: string, combo: string) {
        // First check to see if it exists outside of this ShorcutGroup
        if (this._registeredCombos[combo]) {
            delete this._registeredCombos[combo];
        }

        // If this group does not exist then just return
        if (!this._shortcutsGroups[group]) {
            return;
        }

        const shortcuts = this._shortcutsGroups[group].shortcuts;
        let index = 0;
        let shouldRemove = false;
        for (; index < shortcuts.length; index++) {
            const shortcut = shortcuts[index];

            // Figure out which shortcut the combination was registered for.
            if (shortcut.combos.indexOf(combo) >= 0) {
                // Unbind the shortcut from mouse trap
                if (shortcut.mousetrapInstance) {
                    shortcut.mousetrapInstance.unbind(combo);
                } else {
                    window.Mousetrap.unbind(combo);
                }

                if (shortcut.combos.length === 1) {
                    shouldRemove = true;
                }

                // Remove from global combos if there
                delete this._globalCombos[combo];

                break;
            }
        }

        if (shouldRemove) {
            shortcuts.splice(index, 1);

            if (shortcuts.length === 0) {
                this.removeShortcutGroup(group);
            }
        }
    }

    public removeShortcutGroup(group: string) {
        // If this group does not exist then just return
        if (!this._shortcutsGroups[group]) {
            return;
        }

        const shortcutsGroup = this._shortcutsGroups[group];

        // Remove the group from the lookup dictionary
        delete this._shortcutsGroups[group];

        this.commonRemoveLogic(shortcutsGroup);
    }

    public removeReassignableShortcutGroup(group: string, sequenceNumber: number): void {
        const index = this._reassignableShortcutsGroups.findIndex(g => g.name === group && g.sequenceNumber === sequenceNumber);
        if (index < 0) {
            return;
        }

        const shortcutsGroup = this._reassignableShortcutsGroups[index];   
        this._reassignableShortcutsGroups.splice(index, 1);  
       
        this.commonRemoveLogic(shortcutsGroup);

        const nextReassignableGroup = this.findNextReassignableGroupByName(group);
        if (nextReassignableGroup) {
            const shortcuts = nextReassignableGroup.shortcuts;
            shortcuts.forEach(shortcut => {
                this.registerReassignableGroupShortcut(
                    { name: nextReassignableGroup.name, sequenceNumber: nextReassignableGroup.sequenceNumber },
                    shortcut
                );
            });
        }
    }

    private commonRemoveLogic(shortcutsGroup: IMousetrapShortcutGroup | IMousetrapReassignableShortcutGroup) {
        // If this group does not exist then just return
        if (!shortcutsGroup) {
            return;
        }

        const shortcuts = shortcutsGroup.shortcuts;

        // Unbind its shortcuts from mousetrap
        shortcuts.forEach(shortcut => {
            // Unbind each shortcut from mouse trap
            if (shortcut.mousetrapInstance) {
                shortcut.mousetrapInstance.unbind(shortcut.combos);
            } else {
                window.Mousetrap.unbind(shortcut.combos);
            }

            // Remove the shortcut combos from our cache
            for (const combo of shortcut.combos) {
                delete this._registeredCombos[combo];

                // Remove from global combos if it exists there
                delete this._globalCombos[combo];
            }
        });

    }

    public setShortcutsEnabled(newValue: boolean) {
        trySetLocalStorageValue(KeyboardShortcutsDisabledKey, newValue ? undefined : "true");
        this._shortcutsEnabled.value = newValue;
    }

    private initializeService(): void {
        this.reset();

        // Load the shortcuts which were defined via contribution
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const shortcuts: { [contributionId: string]: IContributedShortcut } =
            contributionService.getContributionsByType<IContributedShortcut>("ms.vss-web.shortcut");

        // if value is undefined in settings, use true
        this._shortcutsEnabled.value = tryGetLocalStorageValue(KeyboardShortcutsDisabledKey) !== "true";

        // Register each of the contribution defined shortcuts
        for (const shortcutId in shortcuts) {
            const contributedShortcut = shortcuts[shortcutId];
            this.registerShortcut(contributedShortcut.group, {
                combos: contributedShortcut.combos,
                description: contributedShortcut.description,
                isPageNavigationShortcut: contributedShortcut.isPageNavigationShortcut,
                action: () => {
                    if (contributedShortcut.command) {
                        contributionService.executeCommandEx(shortcutId, contributedShortcut.command);
                    } else if (contributedShortcut.commandId) {
                        contributionService.executeCommand(contributedShortcut.commandId);
                    }
                }
            });
        }
    }

    private reset() {
        window.Mousetrap.reset();
        this._globalCombos = {};
        this._registeredCombos = {};
        this._shortcutsGroups = {};
        this._reassignableShortcutsGroups = [];
    }
}

Services.add("IVssKeyboardShortcutService", {
    serviceFactory: VssKeyboardShortcutService,
    options: ServiceOptions.Persistant | ServiceOptions.Startup
});
